import { useRender } from '@base-ui/react/use-render'
import { VariantProps } from 'class-variance-authority'
import { createContext, Fragment, isValidElement, useContext, useMemo } from 'react'

import { mergeProps } from '@base-ui/react/merge-props'
import { getValidChildren } from '@eigtech/ui/lib/get-valid-children'
import { cn, cva } from '@eigtech/ui/lib/utils'
import { Separator } from './separator'

type StackContextType = { separatorOrientation: 'horizontal' | 'vertical' }

const StackContext = createContext<StackContextType>({
  separatorOrientation: 'horizontal',
})

/**
 * Visual separator rendered between stack items when `Stack` receives a `separator` prop.
 * Renders a minimal div; use it (or a custom element) as the `separator` to get consistent
 * borders and spacing. Has `data-slot="stack-separator"` for styling.
 */
function StackSeparator(props: React.ComponentProps<'div'>) {
  const { separatorOrientation } = useContext(StackContext)

  return <Separator data-slot="stack-separator" orientation={separatorOrientation} {...props} />
}

const stackVariants = cva('flex gap-2', {
  variants: {
    direction: {
      column:
        'flex-col *:data-[slot=stack-separator]:w-full *:data-[slot=stack-separator]:border-s-0 *:data-[slot=stack-separator]:border-t',
      'column-reverse':
        'flex-col-reverse *:data-[slot=stack-separator]:w-full *:data-[slot=stack-separator]:border-s-0 *:data-[slot=stack-separator]:border-t',
      row: 'flex-row items-center *:data-[slot=stack-separator]:h-full *:data-[slot=stack-separator]:border-s-0 *:data-[slot=stack-separator]:border-t',
      'row-reverse':
        'flex-row-reverse items-center *:data-[slot=stack-separator]:h-full *:data-[slot=stack-separator]:border-s-0 *:data-[slot=stack-separator]:border-t',
    },
  },
  defaultVariants: {
    direction: 'column',
  },
})

/** Variant props for `Stack` (e.g. `direction`: column, row, etc.). */
export type StackVariants = VariantProps<typeof stackVariants>

/** Props for `Stack`, `HStack`, and `VStack`. Supports an optional element to render between children. */
export interface StackProps extends useRender.ComponentProps<'div'>, StackVariants {
  /** Element to render between each child (e.g. `<StackSeparator />` or a custom divider). */
  separator?: React.ReactElement<unknown>
}

/**
 * Flex container that lays out children in a column or row with consistent gap.
 * Optionally renders a `separator` element between each child. Uses `data-slot="stack"`.
 *
 * @example
 * ```tsx
 * <Stack direction="row" separator={<StackSeparator />}>
 *   <span>One</span>
 *   <span>Two</span>
 * </Stack>
 * ```
 */
function Stack({
  className,
  children,
  direction = 'column',
  separator,
  render,
  ...props
}: StackProps) {
  const clones = useMemo(() => {
    if (!isValidElement(separator)) return children
    return getValidChildren(children).map((child, index, arr) => {
      const key = typeof child.key !== 'undefined' ? child.key : index
      return (
        <Fragment key={key}>
          {child}
          {index === arr.length - 1 ? null : separator}
        </Fragment>
      )
    })
  }, [children, separator])

  const component = useRender({
    defaultTagName: 'div',
    render,
    props: mergeProps(
      { className: cn(stackVariants({ direction, className })), children: clones },
      props
    ),
    state: {
      slot: 'stack',
    },
  })

  const context = useMemo(
    () =>
      ({
        separatorOrientation:
          direction === 'column' || direction === 'column-reverse' ? 'horizontal' : 'vertical',
      }) satisfies StackContextType,
    [direction]
  )

  return <StackContext.Provider value={context}>{component}</StackContext.Provider>
}

/**
 * Horizontal stack: `Stack` with `direction="row"`. Lays out children in a row with gap and optional separator.
 */
function HStack(props: StackProps) {
  return <Stack direction="row" {...props} />
}

/**
 * Vertical stack: `Stack` with `direction="column"`. Lays out children in a column with gap and optional separator.
 */
function VStack(props: StackProps) {
  return <Stack direction="column" {...props} />
}

export { HStack, Stack, StackSeparator, VStack }
